import { useEffect, useContext, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { db } from '../../../config/firebase';
import { UserContext } from '../../../context/UserProvider';

/**
 * WaterTankMonitor - Componente que monitorea las operaciones de tanques de agua en segundo plano
 * Este componente funciona independientemente de si el usuario está en la sección WaterTankForm
 * y actualiza automáticamente los estados en Firebase cuando las operaciones terminan.
 */
const WaterTankMonitor = forwardRef(({ onNotification }, ref) => {
  const { usuario, currentMac, canIdIrrigation, togglesUid, switchesUid } = useContext(UserContext);
  const [monitoredTanks, setMonitoredTanks] = useState([]);
  const unsubscribeRefs = useRef({});
  const tankTimeoutsRef = useRef({});
  const stopSentRef = useRef({});

  // (El método getSwitchState no es necesario aquí; los estados se leen vía listener)

  // Función para actualizar el currentStatus en Firebase
  const updateCurrentStatusInDB = useCallback(async (tankId, isRunning, typeOfAction) => {
    try {
      console.log(`[WaterTankMonitor] Actualizando currentStatus para tanque ${tankId}:`, { isRunning, typeOfAction });
      const waterTankAddr = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/waterTankControl`;
      const waterDocRef = db.collection(waterTankAddr).doc("tankRows");

      const snapshot = await waterDocRef.get();
      if (snapshot.exists) {
        const data = snapshot.data();
        let allRows = [...data.allRows];
        console.log("Esto es allROws:",allRows)
        console.log("Esto es typeOfAction:",typeOfAction)

        allRows = allRows.map((row, index) => {
          if (index === tankId) {
            return {
              ...row,
              currentStatus: {
                isRunning: isRunning,
                typeOfAction: typeOfAction
              }
            };
          }
          return row;
        });
        console.log("Esto es despues allRows de currentStatus:",allRows)

        await waterDocRef.set({ allRows });
        console.log(`[WaterTankMonitor] CurrentStatus actualizado en DB para tanque ${tankId}`);
      }
    } catch (error) {
      console.error("[WaterTankMonitor] Error al actualizar currentStatus:", error);
    }
  }, [usuario, currentMac, canIdIrrigation]);

  // Función para actualizar la última ejecución en Firebase
  const updateTankLastExecutionInDB = useCallback(async (tankId, lastExecutionTime, tankAction) => {
    try {
      console.log(`[WaterTankMonitor] Actualizando lastExecution para tanque ${tankId}:`, lastExecutionTime);
      const waterTankAddr = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/waterTankControl`;
      const waterDocRef = db.collection(waterTankAddr).doc("tankRows");

      const snapshot = await waterDocRef.get();
      if (snapshot.exists) {
        const data = snapshot.data();
        let allRows = [...data.allRows];
        console.log("Esto es allRows:",allRows)

        allRows = allRows.map((row, index) => {
          if (index === tankId) {
            return {
              ...row,
              lastExecution: lastExecutionTime,
              lastAction: tankAction
            };
          }
          return row;
        });

        await waterDocRef.set({ allRows });
        console.log(`[WaterTankMonitor] LastExecution actualizado en DB para tanque ${tankId}`);
      }
    } catch (error) {
      console.error("[WaterTankMonitor] Error al actualizar lastExecution:", error);
    }
  }, [usuario, currentMac, canIdIrrigation]);

  // Publicar comando MQTT (vía Firestore) para el módulo
  const sendingTankRoutine = useCallback(async (mqttMessage, moduleId) => {
    try {
      const item = {
        msMqtt: mqttMessage,
        mac: currentMac,
        action: "Tank Routine",
        fecha: Date.now(),
        uid: moduleId
      };
      const addr = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/sendConfigModule`;
      await db.collection(addr).doc("sendConfig").set({ item });
    } catch (error) {
      console.error("[WaterTankMonitor] Error al enviar comando MQTT:", error);
    }
  }, [usuario, currentMac, canIdIrrigation]);

  // Construir y enviar comando STOP para el tanque (recirculación)
  const sendStopCommandForTank = useCallback(async (tank) => {
    try {
      const moduleId = currentMac + "@" + canIdIrrigation;
      const fillData = tank.fillData || tank.fill || {};
      const emptyData = tank.emptyData || tank.empty || {};

      const len = 16;
      const action = 244;
      const typeAction = 2; // STOP
      const estimatedMinutes = 0;
      const estimatedSeconds = 0;
      const totalMinutes = tank.action === "fill" ? (fillData.minutes || 0) : (tank.action === "empty" ? (emptyData.minutes || 0) : 0);
      const totalSeconds = tank.action === "fill" ? (fillData.seconds || 0) : (tank.action === "empty" ? (emptyData.seconds || 0) : 0);
      const minutesWaiting = tank.action === "fill" ? 0 : (emptyData.minutesWaiting || 0);

      const rowIndex = tank.id;
      const recirculationValve = togglesUid.findIndex(uid => uid === (tank.recirculationValve || (tank.details && tank.details.recirculationValve)));
      const fillingPump = togglesUid.findIndex(uid => uid === fillData.pump);
      const recirculationPump = togglesUid.findIndex(uid => uid === (tank.recirculationPump || (tank.details && tank.details.recirculationPump)));
      const fillingValve = togglesUid.findIndex(uid => uid === fillData.fillValve);
      const fillingWaterSourceIndex = togglesUid.findIndex(uid => uid === fillData.waterSource);
      const emptyValve = togglesUid.findIndex(uid => uid === emptyData.emptyValve);
      const emptyPumpIndex = togglesUid.findIndex(uid => uid === emptyData.pump);
      const switchUid = tank.action === "fill" ? fillData.maxSwitch : emptyData.minSwitch;
      const switchIndex = switchesUid.findIndex(uid => uid === switchUid);

      const mqtt =
        len + "," +
        action + "," +
        canIdIrrigation + "," +
        typeAction + "," +
        rowIndex + "," +
        switchIndex + "," +
        estimatedMinutes + "," +
        estimatedSeconds + "," +
        totalMinutes + "," +
        totalSeconds + "," +
        minutesWaiting + "," +
        fillingPump + "," +
        emptyPumpIndex + "," +
        recirculationPump + "," +
        fillingWaterSourceIndex + "," +
        recirculationValve + "," +
        fillingValve + "," +
        emptyValve;

      console.log(`[WaterTankMonitor] Enviando STOP por MQTT para tanque ${tank.name} (recirculación)`);
      await sendingTankRoutine(mqtt, moduleId);
    } catch (error) {
      console.error("[WaterTankMonitor] Error al construir/enviar STOP para tanque:", error);
    }
  }, [canIdIrrigation, currentMac, switchesUid, togglesUid, sendingTankRoutine]);

  // Función para manejar la finalización de una operación
  const handleOperationCompletion = useCallback(async (tank, completionReason, completionTime = null) => {
    const finalCompletionTime = completionTime || new Date().toISOString();
    const actionText = tank.action === "fill" ? "llenado" : (tank.action === "empty" ? "vaciado" : "recirculación");
    
    console.log(`[WaterTankMonitor] Operación de ${actionText} completada para ${tank.name}. Razón: ${completionReason}`);

    // Actualizar estados en Firebase
    await updateCurrentStatusInDB(tank.id, false, null);
    await updateTankLastExecutionInDB(tank.id, finalCompletionTime, tank.action);

    // Limpiar timeout si existe
    if (tankTimeoutsRef.current[tank.id]) {
      clearTimeout(tankTimeoutsRef.current[tank.id]);
      delete tankTimeoutsRef.current[tank.id];
    }

    // Remover tanque de la lista de monitoreados
    setMonitoredTanks(prev => prev.filter(t => t.id !== tank.id));

    // Emitir evento personalizado para comunicación con otros componentes
    const event = new CustomEvent('waterTankOperationComplete', {
      detail: {
        tankId: tank.id,
        tankName: tank.name,
        action: tank.action,
        completionReason: completionReason,
        completionTime: finalCompletionTime
      }
    });
    window.dispatchEvent(event);

    // Enviar notificación si se proporciona la función
    if (onNotification) {
      let message = "";
      switch (completionReason) {
        case "switch":
          const switchType = tank.action === "fill" ? "máximo" : "mínimo";
          message = `La operación de ${actionText} en ${tank.name} ha sido completada automáticamente porque se activó el sensor de nivel ${switchType}.`;
          break;
        case "timeout":
          message = `La operación de ${actionText} en ${tank.name} ha sido completada por tiempo de ejecución.`;
          break;
        case "firebase":
          message = `La operación de ${actionText} en ${tank.name} ha sido completada`;
          break;
        default:
          message = `La operación de ${actionText} en ${tank.name} ha sido completada.`;
      }

      onNotification({
        type: "success",
        title: "Operación completada",
        message: message,
        tankId: tank.id,
        tankName: tank.name,
        action: tank.action
      });
    }
  }, [onNotification, updateCurrentStatusInDB, updateTankLastExecutionInDB]);

  // Función para manejar el paro total de todas las operaciones
  const handleEmergencyStop = useCallback(async (completionTime = null) => {
    const finalCompletionTime = completionTime || new Date().toISOString();

    console.log(`[WaterTankMonitor] PARO TOTAL DETECTADO - Deteniendo todas las operaciones activas`);

    // Crear una copia de los tanques monitoreados para evitar problemas de concurrencia
    const tanksToStop = [...monitoredTanks];
    console.log("Esto es tanksToStop:", tanksToStop);
    // Detener cada tanque activo
    for (const tank of tanksToStop) {
      const actionText = tank.action === "fill" ? "llenado" : (tank.action === "empty" ? "vaciado" : "recirculación");

      console.log(`[WaterTankMonitor] Deteniendo operación de ${actionText} para tanque ${tank.name} por paro total`);

      console.log("Esto es tank.action:", tank.action);
      // Actualizar estados en Firebase
      await updateCurrentStatusInDB(tank.id, false, null);
      await updateTankLastExecutionInDB(tank.id, finalCompletionTime, tank.action);

      // Limpiar timeout si existe
      if (tankTimeoutsRef.current[tank.id]) {
        clearTimeout(tankTimeoutsRef.current[tank.id]);
        delete tankTimeoutsRef.current[tank.id];
      }

      // Limpiar listeners específicos del tanque
      if (unsubscribeRefs.current[`switch_${tank.id}`]) {
        unsubscribeRefs.current[`switch_${tank.id}`]();
        delete unsubscribeRefs.current[`switch_${tank.id}`];
      }

      if (unsubscribeRefs.current[`firebase_${tank.id}`]) {
        unsubscribeRefs.current[`firebase_${tank.id}`]();
        delete unsubscribeRefs.current[`firebase_${tank.id}`];
      }
    }

    // Limpiar la lista de tanques monitoreados
    setMonitoredTanks([]);

    // Emitir evento personalizado para comunicación con otros componentes
    const event = new CustomEvent('waterTankEmergencyStop', {
      detail: {
        stoppedTanks: tanksToStop.map(t => ({ id: t.id, name: t.name, action: t.action })),
        completionTime: finalCompletionTime,
        reason: "Paro total de emergencia"
      }
    });
    window.dispatchEvent(event);

    // Enviar notificación global si se proporciona la función
    if (onNotification) {
      const stoppedCount = tanksToStop.length;
      const message = stoppedCount > 0
        ? `Se han detenido ${stoppedCount} operación${stoppedCount > 1 ? 'es' : ''} de tanque${stoppedCount > 1 ? 's' : ''} debido a un paro total de emergencia.`
        : "Se ha ejecutado un paro total de emergencia en el sistema de tanques.";

      onNotification({
        type: "warning",
        title: "Paro Total de Emergencia",
        message: message,
        isEmergencyStop: true,
        stoppedTanks: tanksToStop
      });
    }

    // Limpiar el documento de Firebase después de 5 segundos para evitar ciclos infinitos
    // Esto es crucial para evitar que el listener siga detectando el paro de emergencia
    setTimeout(async () => {
      try {
        console.log(`[WaterTankMonitor] Limpiando documento de paro de emergencia después de 5 segundos`);
        const docPath = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/fromModule/configOK`;
        const docRef = db.doc(docPath);

        await docRef.update({
          accion: 0,
          act: "-"
        });

        console.log(`[WaterTankMonitor] Documento de paro de emergencia limpiado exitosamente`);
      } catch (error) {
        console.error(`[WaterTankMonitor] Error al limpiar documento de paro de emergencia:`, error);
      }
    }, 6000);

  }, [monitoredTanks, onNotification, updateCurrentStatusInDB, updateTankLastExecutionInDB, usuario, currentMac, canIdIrrigation]);

  const handleTimeoutCompletion = useCallback((tank) => {
    if (onNotification) {
      let message = `Finalizo el tiempo de espera y la operación de vaciado en ${tank.name} ha iniciado.`;

      onNotification({
        type: "info",
        title: "Incio el vaciado",
        message: message,
        tankId: tank.id,
        tankName: tank.name,
        action: tank.action
      });
    }
  }, [onNotification]);

  // Función para configurar el timeout de una operación
  const setupOperationTimeout = useCallback((tank) => {
    const totalTimeMs = (tank.totalMinutes * 60 + tank.totalSeconds) * 1000;
    
    if (totalTimeMs > 0) {
      tankTimeoutsRef.current[tank.id] = setTimeout(() => {
        console.log(`[WaterTankMonitor] Timeout alcanzado para tanque ${tank.name}`);
        handleOperationCompletion(tank, "timeout");
      }, totalTimeMs);
      
      console.log(`[WaterTankMonitor] Timeout configurado para tanque ${tank.name}: ${totalTimeMs}ms`);
    }
  }, [handleOperationCompletion]);

  // Función para monitorear switches de un tanque
  const monitorTankSwitches = useCallback((tank) => {
    const switchUid = tank.action === "fill" ? tank.fillData?.maxSwitch : tank.emptyData?.minSwitch;
    
    if (!switchUid) {
      console.log(`[WaterTankMonitor] No hay switch configurado para tanque ${tank.name}`);
      return;
    }

    const arrayDeCadenas = switchUid.split("@");
    const mac = arrayDeCadenas[0];
    const canId = arrayDeCadenas[1];
    
    const docPath = `${usuario.username}/infoDevices/${mac}/${canId}/fromModule/switchesInfo`;
    const docRef = db.doc(docPath);

    const unsubscribe = docRef.onSnapshot((snapshot) => {
      if (snapshot.exists) {
        const data = snapshot.data();
        if (data && data.states) {
          const outId = Number(arrayDeCadenas[3]);
          const switchState = data.states[outId];
          
          // Verificar condición de finalización/paro según el tipo de operación
          if (tank.action === "fill" && switchState === 1) {
            console.log(`[WaterTankMonitor] Switch máximo activado en ${tank.name}, completando llenado`);
            handleOperationCompletion(tank, "switch");
          } else if (tank.action === "empty" && switchState === 0) {
            console.log(`[WaterTankMonitor] Switch mínimo desactivado en ${tank.name}, completando vaciado`);
            handleOperationCompletion(tank, "switch");
          } else if (tank.action === "recirculate" && switchState === 0) {
            // Detener recirculación cuando el nivel mínimo se desactiva
            if (!stopSentRef.current[tank.id]) {
              stopSentRef.current[tank.id] = true;
              console.log(`[WaterTankMonitor] Switch mínimo desactivado en ${tank.name}, enviando STOP de recirculación`);
              sendStopCommandForTank(tank);
            }
          }
        }
      }
    });

    unsubscribeRefs.current[`switch_${tank.id}`] = unsubscribe;
  }, [sendStopCommandForTank, usuario, handleOperationCompletion]);

  // Función para monitorear confirmaciones MQTT de Firebase
  const monitorFirebaseConfirmations = useCallback((tank) => {
    const docPath = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/fromModule/configOK`;
    const docRef = db.doc(docPath);

    const unsubscribe = docRef.onSnapshot(async (snapshot) => {
      if (snapshot.exists) {
        const data = snapshot.data();
        const cleanData = {
          ...data,        // copiamos todas las propiedades
          accion: 0,      // sobreescribimos solo las que quieres cambiar
          act: "-"        // sobreescribimos solo las que quieres cambiar
        };

        if (data) {
          // Verificar parámetros específicos para detención de operación
          const actionKind = tank.action === "fill" ? 1 : (tank.action === "empty" ? 0 : 3);

          if (
            data.accion === 244 &&
            data.act === "recOK" &&
            data.kind === actionKind &&
            data.outid === tank.id &&
            data.val === 0
          ) {
            console.log(`[WaterTankMonitor] Confirmación Firebase recibida para tanque ${tank.name}`);
            handleOperationCompletion(tank, "firebase");
            // Restablecer bandera para permitir futuros stops
            if (stopSentRef.current[tank.id]) delete stopSentRef.current[tank.id];

            // Emitir evento para que la UI actualice el botón (mismo contracto que WaterTankForm)
            try {
              const event = new CustomEvent('mqttValidationSuccess', {
                detail: { tankId: tank.id, isExecuting: true, tank: { ...tank } }
              });
              window.dispatchEvent(event);
              await docRef.update(cleanData);
            } catch (e) {
              console.warn('[WaterTankMonitor] No se pudo emitir mqttValidationSuccess:', e);
            }
          } else if (
            data.accion === 244 &&
            data.act === "recOK" &&
            data.kind === 0 &&
            data.outid === tank.id &&
            data.val === 2
          ) {
            console.log(`[WaterTankMonitor] Termino tiempo de espera de recirculcion para tanque ${tank.name}`);
            handleTimeoutCompletion(tank);
            // const cleanData = {
            //   ...data,        // copiamos todas las propiedades
            //   accion: 0,      // sobreescribimos solo las que quieres cambiar
            //   act: "-"        // sobreescribimos solo las que quieres cambiar
            // };
            await docRef.update(cleanData);
          }
          // Nota: La detección de paro total (kind: 4) se maneja en el listener global
          // para evitar duplicación de lógica en cada tanque individual
        }
      }
    });

    unsubscribeRefs.current[`firebase_${tank.id}`] = unsubscribe;
  }, [canIdIrrigation, currentMac, usuario, handleOperationCompletion, handleTimeoutCompletion]);

  // Función para iniciar el monitoreo de un tanque
  const startMonitoring = useCallback((tank) => {
    console.log(`[WaterTankMonitor] Iniciando monitoreo para tanque ${tank.name}, acción: ${tank.action}`);
    
    // Solo monitorear operaciones de llenado y vaciado (no recirculación)
    if (tank.action === "fill" || tank.action === "empty" || tank.action === "recirculate") {
      setupOperationTimeout(tank);
      monitorTankSwitches(tank);
      monitorFirebaseConfirmations(tank);
    }
  }, [monitorFirebaseConfirmations, monitorTankSwitches, setupOperationTimeout]);

  // Función para detener el monitoreo de un tanque
  const stopMonitoring = useCallback((tankId) => {
    console.log(`[WaterTankMonitor] Deteniendo monitoreo para tanque ${tankId}`);
    
    // Limpiar listeners
    if (unsubscribeRefs.current[`switch_${tankId}`]) {
      unsubscribeRefs.current[`switch_${tankId}`]();
      delete unsubscribeRefs.current[`switch_${tankId}`];
    }
    
    if (unsubscribeRefs.current[`firebase_${tankId}`]) {
      unsubscribeRefs.current[`firebase_${tankId}`]();
      delete unsubscribeRefs.current[`firebase_${tankId}`];
    }

    // Limpiar timeout
    if (tankTimeoutsRef.current[tankId]) {
      clearTimeout(tankTimeoutsRef.current[tankId]);
      delete tankTimeoutsRef.current[tankId];
    }
  }, []);

  // Efecto para cargar y monitorear tanques activos
  useEffect(() => {
    if (!usuario || !usuario.username || !currentMac || !canIdIrrigation) {
      return;
    }

    const loadActiveTanks = async () => {
      try {
        const waterTankAddr = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/waterTankControl`;
        const waterDocRef = db.collection(waterTankAddr).doc("tankRows");
        const snapshot = await waterDocRef.get();

        if (snapshot.exists) {
          const data = snapshot.data();
          const allRows = data.allRows || [];
          
          // Filtrar tanques que están ejecutándose
          const activeTanks = allRows
            .map((row, index) => ({ ...row, id: index }))
            .filter(tank => tank.currentStatus && tank.currentStatus.isRunning);

          console.log(`[WaterTankMonitor] Tanques activos encontrados:`, activeTanks.length);
          
          // Actualizar lista de tanques monitoreados
          setMonitoredTanks(activeTanks);
          
          // Iniciar monitoreo para cada tanque activo
          activeTanks.forEach(tank => {
            // Agregar información adicional necesaria para el monitoreo
            const enhancedTank = {
              ...tank,
              action: tank.currentStatus.typeOfAction,
              totalMinutes: tank.action === "fill" ? tank.fill?.totalMinutes || 0 : tank.empty?.totalMinutes || 0,
              totalSeconds: tank.action === "fill" ? tank.fill?.totalSeconds || 0 : tank.empty?.totalSeconds || 0,
              fillData: tank.fill,
              emptyData: tank.empty
            };
            
            startMonitoring(enhancedTank);
          });
        }
      } catch (error) {
        console.error("[WaterTankMonitor] Error al cargar tanques activos:", error);
      }
    };

    loadActiveTanks();

    // Cleanup al desmontar
    return () => {
      Object.values(unsubscribeRefs.current).forEach(unsubscribe => unsubscribe());
      Object.values(tankTimeoutsRef.current).forEach(timeout => clearTimeout(timeout));
      unsubscribeRefs.current = {};
      tankTimeoutsRef.current = {};
    };
  }, [usuario, currentMac, canIdIrrigation, startMonitoring]);

  // Función pública para agregar un tanque al monitoreo (llamada desde WaterTankForm)
  const addTankToMonitoring = useCallback((tank) => {
    console.log(`[WaterTankMonitor] Agregando tanque al monitoreo:`, tank.name);

    // Verificar si ya está siendo monitoreado
    const isAlreadyMonitored = monitoredTanks.some(t => t.id === tank.id);
    if (isAlreadyMonitored) {
      console.log(`[WaterTankMonitor] Tanque ${tank.name} ya está siendo monitoreado`);
      return;
    }

    // Agregar a la lista de monitoreados
    setMonitoredTanks(prev => [...prev, tank]);

    // Iniciar monitoreo
    startMonitoring(tank);
  }, [monitoredTanks, startMonitoring]);

  // Función pública para remover un tanque del monitoreo
  const removeTankFromMonitoring = useCallback((tankId) => {
    console.log(`[WaterTankMonitor] Removiendo tanque del monitoreo:`, tankId);

    stopMonitoring(tankId);
    setMonitoredTanks(prev => prev.filter(t => t.id !== tankId));
  }, [stopMonitoring]);

  // Exponer funciones públicas a través de useImperativeHandle si es necesario
  useImperativeHandle(ref, () => ({
    addTankToMonitoring,
    removeTankFromMonitoring,
    getMonitoredTanks: () => monitoredTanks
  }), [monitoredTanks, addTankToMonitoring, removeTankFromMonitoring]);

  // Listener para cambios en el documento de tanques (para detectar nuevas operaciones)
  useEffect(() => {
    if (!usuario || !usuario.username || !currentMac || !canIdIrrigation) {
      return;
    }

    const waterTankAddr = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/waterTankControl`;
    const waterDocRef = db.collection(waterTankAddr).doc("tankRows");

    const unsubscribe = waterDocRef.onSnapshot((snapshot) => {
      if (snapshot.exists) {
        const data = snapshot.data();
        const allRows = data.allRows || [];

        // Buscar tanques que empezaron a ejecutarse y no están siendo monitoreados
        allRows.forEach((row, index) => {
          const tank = { ...row, id: index };

          if (tank.currentStatus && tank.currentStatus.isRunning) {
            const isAlreadyMonitored = monitoredTanks.some(t => t.id === tank.id);

            if (!isAlreadyMonitored && (tank.currentStatus.typeOfAction === "fill" || tank.currentStatus.typeOfAction === "empty" || tank.currentStatus.typeOfAction === "recirculate")) {
              console.log(`[WaterTankMonitor] Nueva operación detectada para tanque ${tank.name || tank.id}`);

              // Agregar información adicional necesaria
              const enhancedTank = {
                ...tank,
                action: tank.currentStatus.typeOfAction,
                totalMinutes: tank.currentStatus.typeOfAction === "fill" ? tank.fill?.totalMinutes || 0 : tank.empty?.totalMinutes || 0,
                totalSeconds: tank.currentStatus.typeOfAction === "fill" ? tank.fill?.totalSeconds || 0 : tank.empty?.totalSeconds || 0,
                fillData: tank.fill,
                emptyData: tank.empty
              };

              addTankToMonitoring(enhancedTank);
            }
          } else {
            // Si el tanque ya no está ejecutándose, removerlo del monitoreo
            const wasMonitored = monitoredTanks.some(t => t.id === tank.id);
            if (wasMonitored) {
              console.log(`[WaterTankMonitor] Tanque ${tank.name || tank.id} ya no está ejecutándose, removiendo del monitoreo`);
              removeTankFromMonitoring(tank.id);
            }
          }
        });
      }
    });

    return () => unsubscribe();
  }, [usuario, currentMac, canIdIrrigation, monitoredTanks, startMonitoring, removeTankFromMonitoring, addTankToMonitoring]);

  // Listener global para detectar paro total de emergencia
  useEffect(() => {
    if (!usuario || !usuario.username || !currentMac || !canIdIrrigation) {
      return;
    }

    const docPath = `${usuario.username}/infoDevices/${currentMac}/${canIdIrrigation}/fromModule/configOK`;
    const docRef = db.doc(docPath);

    const unsubscribe = docRef.onSnapshot((snapshot) => {
      if (snapshot.exists) {
        const data = snapshot.data();

        if (data) {
          // Detectar paro total de emergencia
          if (
            data.accion === 244 &&
            data.act === "recOK" &&
            data.kind === 4 &&
            data.val === 0
          ) {
            console.log(`[WaterTankMonitor] PARO TOTAL DETECTADO - Parámetros:`, data);
            handleEmergencyStop();
          }
        }
      }
    });

    // Guardar la referencia del listener global
    unsubscribeRefs.current['emergencyStopListener'] = unsubscribe;

    return () => {
      if (unsubscribeRefs.current['emergencyStopListener']) {
        unsubscribeRefs.current['emergencyStopListener']();
        delete unsubscribeRefs.current['emergencyStopListener'];
      }
    };
  }, [usuario, currentMac, canIdIrrigation, handleEmergencyStop]);

  // Log de estado actual para debugging
  useEffect(() => {
    if (monitoredTanks.length > 0) {
      console.log(`[WaterTankMonitor] Tanques actualmente monitoreados:`,
        monitoredTanks.map(t => ({ id: t.id, name: t.name, action: t.action }))
      );
    }
  }, [monitoredTanks]);

  // Este componente no renderiza nada visible
  return null;
});

export default WaterTankMonitor;
